import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Button,
  Chip,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  ChatbotTemplateActionMarkup,
  ChatbotTemplateAction,
  ChatbotTemplateActionType,
} from '../../model/types';

export interface ActionsEditorProps {
  actions: ChatbotTemplateAction[];
  onChange: (actions: ChatbotTemplateAction[]) => void;
  disabled?: boolean;
  actionMarkup: ChatbotTemplateActionMarkup;
  deepLinkTargets: string[];
  /** Read-only actions the bot appends itself after the merchant's own (templates doc §13.6). */
  hubActions: { type: ChatbotTemplateActionType; value: string }[];
  /** Types the flow expects the template to carry (advisory) — offers "Add {{action}}" when missing. */
  expectedActionTypes: ChatbotTemplateActionType[];
  contentLanguageLabel: string;
}

const INLINE_TYPES: ChatbotTemplateActionType[] = ['BUTTON', 'URL', 'DEEP_LINK'];
const REPLY_TYPES: ChatbotTemplateActionType[] = ['QUICK_REPLY', 'REQUEST_CONTACT'];
const MAX_ACTIONS = 8;

function isInline(type: ChatbotTemplateActionType): boolean {
  return INLINE_TYPES.includes(type);
}

function hubActionCaption(
  action: { type: ChatbotTemplateActionType; value: string },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (action.type === 'DEEP_LINK' && action.value === 'catalog')
    return t('editor.actions.hub.catalog');
  if (action.type === 'DEEP_LINK' && action.value.startsWith('order:'))
    return t('editor.actions.hub.order');
  if (action.type === 'QUICK_REPLY' && action.value.startsWith('/{'))
    return t('editor.actions.hub.commandMenu');
  if (action.type === 'BUTTON' && action.value.startsWith('lang:'))
    return t('editor.actions.hub.language');
  return t('editor.actions.hub.other', { type: action.type, value: action.value });
}

/**
 * Up to 8 button/quick-reply/URL/deep-link/request-contact rows, constrained per key by
 * `actionMarkup` (templates doc §13.6) — `NONE` hides the editor entirely, `INLINE`/`REPLY` offer
 * only their own type family, `ANY` groups both and disables the other family once one row exists.
 * The bot's own `hubActions` render read-only underneath; `expectedActionTypes` missing from the
 * draft offer a one-click "Add …" prompt.
 */
export function ActionsEditor({
  actions,
  onChange,
  disabled = false,
  actionMarkup,
  deepLinkTargets,
  hubActions,
  expectedActionTypes,
  contentLanguageLabel,
}: ActionsEditorProps) {
  const { t } = useTranslation('chatbot');

  const update = (index: number, patch: Partial<ChatbotTemplateAction>) => {
    onChange(actions.map((action, i) => (i === index ? { ...action, ...patch } : action)));
  };
  const remove = (index: number) => onChange(actions.filter((_action, i) => i !== index));
  const hasInline = actions.some((a) => isInline(a.type));
  const hasReply = actions.some((a) => !isInline(a.type));

  const availableTypes: ChatbotTemplateActionType[] =
    actionMarkup === 'INLINE'
      ? INLINE_TYPES
      : actionMarkup === 'REPLY'
        ? REPLY_TYPES
        : actionMarkup === 'ANY'
          ? [...INLINE_TYPES, ...REPLY_TYPES]
          : [];

  const add = () => {
    const type =
      availableTypes.find((candidate) => (hasReply ? isInline(candidate) === false : true)) ??
      availableTypes[0];
    onChange([...actions, { type: type ?? 'BUTTON', label: '', value: '' }]);
  };

  const addExpected = (type: ChatbotTemplateActionType) => {
    onChange([...actions, { type, label: '', value: '' }]);
  };

  if (actionMarkup === 'NONE') {
    const forbidden = actions.length > 0;
    return (
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('editor.actions.noneAllowed')}
        </Typography>
        {forbidden ? (
          <>
            {actions.map((action, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`${action.type}: ${action.label}`} />
                <Typography variant="caption" color="warning.main">
                  {t('editor.actions.notAllowedRow')}
                </Typography>
              </Stack>
            ))}
            <Button size="small" color="error" onClick={() => onChange([])} disabled={disabled}>
              {t('editor.actions.removeAll')}
            </Button>
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{t('editor.actionsLabel')}</Typography>
      {actionMarkup === 'INLINE' ? (
        <Typography variant="caption" color="text.secondary">
          {t('editor.actions.inlineOnly')}
        </Typography>
      ) : actionMarkup === 'REPLY' ? (
        <Typography variant="caption" color="text.secondary">
          {t('editor.actions.replyOnly')}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t('editor.actions.noMix')}
        </Typography>
      )}

      {actions.map((action, index) => {
        const rowInline = isInline(action.type);
        const rowNotAllowed =
          (actionMarkup === 'INLINE' && !rowInline) ||
          (actionMarkup === 'REPLY' && rowInline) ||
          (actionMarkup === 'ANY' &&
            ((rowInline && hasReply && actions.slice(0, index).some((a) => !isInline(a.type))) ||
              (!rowInline && hasInline && actions.slice(0, index).some((a) => isInline(a.type)))));
        return (
          <Stack key={index} spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                select
                size="small"
                label={t('editor.actionType')}
                value={action.type}
                disabled={disabled}
                onChange={(event) =>
                  update(index, { type: event.target.value as ChatbotTemplateActionType })
                }
                sx={{ minWidth: 170 }}
              >
                {actionMarkup === 'ANY'
                  ? [
                      <ListSubheader key="inline-header">
                        {t('editor.actions.groupInline')}
                      </ListSubheader>,
                      ...INLINE_TYPES.map((type) => (
                        <MenuItem key={type} value={type} disabled={hasReply}>
                          {t(`editor.actionTypeOptions.${type}`)}
                        </MenuItem>
                      )),
                      <ListSubheader key="reply-header">
                        {t('editor.actions.groupReply')}
                      </ListSubheader>,
                      ...REPLY_TYPES.map((type) => (
                        <MenuItem key={type} value={type} disabled={hasInline}>
                          {t(`editor.actionTypeOptions.${type}`)}
                        </MenuItem>
                      )),
                    ]
                  : availableTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {t(`editor.actionTypeOptions.${type}`)}
                      </MenuItem>
                    ))}
              </TextField>
              <TextField
                size="small"
                label={t('editor.actionLabel')}
                value={action.label}
                disabled={disabled}
                onChange={(event) => update(index, { label: event.target.value })}
                helperText={t('editor.actions.labelIn', { language: contentLanguageLabel })}
                slotProps={{ htmlInput: { maxLength: 64 } }}
                sx={{ minWidth: 160 }}
              />
              {action.type === 'REQUEST_CONTACT' ? null : action.type === 'DEEP_LINK' ? (
                <TextField
                  select
                  size="small"
                  label={t('editor.actionValue')}
                  value={action.value}
                  disabled={disabled}
                  error={action.value !== '' && !deepLinkTargets.includes(action.value)}
                  helperText={
                    action.value !== '' && !deepLinkTargets.includes(action.value)
                      ? t('editor.deepLinkTargetUnsupported')
                      : undefined
                  }
                  onChange={(event) => update(index, { value: event.target.value })}
                  sx={{ minWidth: 160 }}
                >
                  {!deepLinkTargets.includes(action.value) && action.value !== '' ? (
                    <MenuItem value={action.value}>{action.value}</MenuItem>
                  ) : null}
                  {deepLinkTargets.map((target) => (
                    <MenuItem key={target} value={target}>
                      {t(`editor.deepLinkTargets.${target}`, target)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  size="small"
                  type={action.type === 'URL' ? 'url' : 'text'}
                  label={t('editor.actionValue')}
                  value={action.value}
                  disabled={disabled}
                  onChange={(event) => update(index, { value: event.target.value })}
                  slotProps={{ htmlInput: { maxLength: 512 } }}
                  sx={{ minWidth: 200 }}
                />
              )}
              <IconButton
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => remove(index)}
                aria-label={t('editor.removeAction', { index: index + 1 })}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
            {rowNotAllowed ? (
              <Typography variant="caption" color="warning.main">
                {t('editor.actions.notAllowedRow')}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
      <Button
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={add}
        disabled={disabled || actions.length >= MAX_ACTIONS}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('editor.addAction')}
      </Button>

      {hubActions.length > 0 ? (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('editor.actions.addedByBot')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {hubActions.map((action, index) => (
              <Chip
                key={index}
                size="small"
                variant="outlined"
                sx={{ borderStyle: 'dashed' }}
                label={hubActionCaption(action, t)}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}

      {expectedActionTypes
        .filter((type) => !actions.some((a) => a.type === type))
        .map((type) => (
          <Alert
            key={type}
            severity="info"
            action={
              <Button size="small" onClick={() => addExpected(type)} disabled={disabled}>
                {t('editor.actions.addExpected', { action: t(`editor.actionTypeOptions.${type}`) })}
              </Button>
            }
          >
            {t('editor.actions.expectedMissing', { action: t(`editor.actionTypeOptions.${type}`) })}
          </Alert>
        ))}
    </Stack>
  );
}
